<script setup lang="ts">
/**
 * UI-kit component
 * Category: Sliders
 * Name: CardSlider (with SliderNav)
 * FIGMA: https://www.figma.com/design/NgI2cVJkWODrgnOBHO3Zed/Strapi-Builder--DS-?node-id=3174-18969
 *
 * Shared horizontal scroll wrapper for any row of cards, arrows beneath (SliderNav).
 * The default slot holds the card row; each slotted child is one snap stop.
 * Soft snap (proximity, align start), one arrow click advances exactly one card,
 * the inactive arrow stays in the keyboard order (aria-disabled, never removed).
 * Full-bleed overflow is controlled by the consumer via the --card-slider-bleed CSS var.
 * scrollDriven: vertical page scroll drives the row horizontally while the slider
 * crosses the viewport; stops after the first user interaction or reduced motion.
 * Local implementation of the Figma DS CardSlider/SliderNav missing from
 * @brighterly/lib-lumee-ui (dsGap) — reviews and tutors share it, never build a second slider.
 */
// types
import type { CardSliderProps } from '../types/interfaces'
// hooks
import { useCardSlider } from '../composables/use-card-slider.hook'
// components
import { Icon } from '@brighterly/lib-lumee-ui'

defineOptions({
  name: 'CardSlider',
})

const props = withDefaults(defineProps<CardSliderProps>(), {
  prevLabel: 'Previous cards',
  nextLabel: 'Next cards',
  scrollDriven: false,
})

const emit = defineEmits<{
  'click:prev': []
  'click:next': []
}>()

const {
  refRoot,
  refTrack,
  refCanScrollPrev,
  refCanScrollNext,
  handleClickPrev,
  handleClickNext,
} = useCardSlider({
  props,
  onPrev: () => emit('click:prev'),
  onNext: () => emit('click:next'),
})
</script>

<template>
  <div
    ref="refRoot"
    class="card-slider"
  >
    <div
      ref="refTrack"
      class="card-slider__track"
    >
      <slot />
    </div>

    <div class="card-slider__nav">
      <button
        type="button"
        class="card-slider__arrow card-slider__arrow--prev"
        :aria-label="prevLabel"
        :aria-disabled="!refCanScrollPrev ? 'true' : undefined"
        @click="handleClickPrev"
      >
        <span class="card-slider__arrow-box">
          <Icon
            name="arrow-left"
            size="lg"
            weight="solid"
            color="inherit"
          />
        </span>
      </button>
      <button
        type="button"
        class="card-slider__arrow card-slider__arrow--next"
        :aria-label="nextLabel"
        :aria-disabled="!refCanScrollNext ? 'true' : undefined"
        @click="handleClickNext"
      >
        <span class="card-slider__arrow-box">
          <Icon
            name="arrow-right"
            size="lg"
            weight="solid"
            color="inherit"
          />
        </span>
      </button>
    </div>
  </div>
</template>

<style lang="scss" scoped src="../styles/_card-slider.scss" />
